import { useCallback, useState } from 'react';

/**
 * View mode for the tile-grid starting surfaces (/apps, /farm, /documents).
 * Named `TileViewMode` (not the bare `ViewMode` already used by the docs
 * new-filter SortLabel) to avoid a public-API name collision.
 */
export type TileViewMode = 'card' | 'compact';

/**
 * ONE shared preference across all three tile grids (#1893). The user's
 * mental model is a single "how do I like to browse tile grids" choice
 * (Finder/Drive-style), so a per-page key would just triple the state for
 * no benefit.
 */
export const TILE_VIEW_MODE_LS_KEY = 'hofsync.tiles.view-mode';

const DEFAULT_MODE: TileViewMode = 'card';

function isTileViewMode(value: unknown): value is TileViewMode {
    return value === 'card' || value === 'compact';
}

/**
 * Read the persisted view mode synchronously. Guards `window` (jsdom/SSR)
 * and swallows storage errors (private mode / quota), degrading to the
 * default. Exported so the caller can read the same value in a lazy
 * `useState` initializer without duplicating the guard logic.
 */
export function readPersistedTileViewMode(): TileViewMode {
    if (typeof window === 'undefined') return DEFAULT_MODE;
    try {
        const raw = window.localStorage.getItem(TILE_VIEW_MODE_LS_KEY);
        if (isTileViewMode(raw)) return raw;
    } catch {
        /* swallow storage errors (private mode, quota) */
    }
    return DEFAULT_MODE;
}

export interface UseTileViewMode {
    mode: TileViewMode;
    setMode: (next: TileViewMode) => void;
}

/**
 * Persisted card/compact view-mode hook for the tile grids.
 *
 * No-flash contract: the persisted value is read in the lazy `useState`
 * initializer, so the very first render already paints the correct view —
 * NOT in a `useEffect` (which would paint the default first, then correct,
 * i.e. the exact flash the AC forbids). The write mirrors the ChatProvider
 * idiom (persist in the setter, not a mount effect) so there is no
 * redundant write-on-mount.
 */
export function useTileViewMode(): UseTileViewMode {
    const [mode, setModeState] = useState<TileViewMode>(readPersistedTileViewMode);

    const setMode = useCallback((next: TileViewMode) => {
        setModeState(next);
        try {
            window.localStorage.setItem(TILE_VIEW_MODE_LS_KEY, next);
        } catch {
            /* swallow storage errors — in-memory state still flips */
        }
    }, []);

    return { mode, setMode };
}
